import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useLanguage } from "@/contexts/LanguageContext";
import { UI_TRANSLATIONS } from "@/lib/i18n";

interface HeroData {
  name: string;
  image: string;
  onClick?: () => void;
}

interface HeroSectionProps {
  heroes: HeroData[];
}

const AUTOPLAY_DELAY_MS = 6000;

export const HeroSection = ({ heroes }: HeroSectionProps) => {
  const { language } = useLanguage();
  const translations = UI_TRANSLATIONS[language];
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const updateActiveSlide = () => {
      setActiveIndex(carouselApi.selectedScrollSnap());
    };

    updateActiveSlide();
    carouselApi.on("select", updateActiveSlide);
    carouselApi.on("reInit", updateActiveSlide);

    return () => {
      carouselApi.off("select", updateActiveSlide);
      carouselApi.off("reInit", updateActiveSlide);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi || heroes.length <= 1) return;

    const autoplay = window.setInterval(() => {
      carouselApi.scrollNext();
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearInterval(autoplay);
  }, [carouselApi, heroes.length]);

  return (
    <section className="hero-carousel" aria-label={translations.highlights}>
      <Carousel
        opts={{ align: "start", loop: true }}
        setApi={setCarouselApi}
        className="hero-carousel-viewport"
      >
        <CarouselContent>
          {heroes.map((hero, index) => (
            <CarouselItem key={`${hero.name}-${index}`}>
              <article className="hero-slide">
                <button
                  type="button"
                  className="hero-slide-media"
                  onClick={hero.onClick}
                  disabled={!hero.onClick}
                  aria-label={hero.onClick ? `Abrir ${hero.name}` : undefined}
                >
                  <img
                    src={hero.image}
                    alt=""
                    className="municipality-image"
                  />
                </button>
                <h1 className="municipality-name">{hero.name}</h1>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {heroes.length > 1 && (
        <div className="hero-carousel-dots" aria-label={translations.selectImage}>
          {heroes.map((hero, index) => (
            <button
              key={`${hero.name}-dot-${index}`}
              type="button"
              className={`hero-carousel-dot ${index === activeIndex ? "is-active" : ""}`}
              onClick={() => carouselApi?.scrollTo(index)}
              aria-label={`${translations.selectImage} ${index + 1}: ${hero.name}`}
              aria-current={index === activeIndex ? "true" : undefined}
            />
          ))}
        </div>
      )}
    </section>
  );
};
